import { Input } from '@/lib/Input';
import { Children, ComponentProps, isValidElement, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { BaseModal } from '../BaseModal';
import { ModalDescription, ModalPattern, ModalTitle } from '../common';
import { DeleteModalInputHint } from './DeleteModalInputHint';
import { DeleteModalWithConfirmInputFooter } from './DeleteModalWithConfirmInputFooter';
import { DeleteModalWithConfirmInputIcon } from './DeleteModalWithConfirmInputIcon';

type DeleteModalWithConfirmInputProps = { inputComparisonValue: string } & ComponentProps<typeof BaseModal> &
  Omit<ComponentProps<typeof DeleteModalWithConfirmInputFooter>, 'onCancel' | 'canConfirm'>;

// React Compound Component Pattern best practices suggest to use function declaration instead of arrow functions
export function DeleteModalWithConfirmInput({
  children,
  inputComparisonValue,
  onClose,
  classNames,
  isDismissable,
  ...footerProps
}: DeleteModalWithConfirmInputProps) {
  let title = null;
  let description = null;
  let inputHint = null;

  Children.forEach(children, (child) => {
    if (isValidElement(child)) {
      if (child.type === ModalTitle) {
        title = child;
      } else if (child.type === ModalDescription) {
        description = child;
      } else if (child.type === DeleteModalInputHint) {
        inputHint = child;
      }
    }
  });

  const [inputValue, setInputValue] = useState<string>('');
  const allowConfirm = inputValue === inputComparisonValue;

  return (
    <BaseModal
      onClose={onClose}
      classNames={{
        content: twMerge('max-w-[22rem] min-h-64 gap-8', classNames?.content),
        ...classNames,
      }}
      isDismissable={isDismissable}
    >
      <ModalPattern />
      <div className='flex flex-col gap-5'>
        <DeleteModalWithConfirmInputIcon />
        <div className='flex flex-col gap-5'>
          <div>
            {title}
            {description}
          </div>
          <div className='flex flex-col gap-1.5'>
            {inputHint}
            <Input onChange={(e) => setInputValue(e.currentTarget.value)} />
          </div>
        </div>
      </div>
      <DeleteModalWithConfirmInputFooter canConfirm={allowConfirm} onClose={onClose} {...footerProps} />
    </BaseModal>
  );
}

DeleteModalWithConfirmInput.Title = ModalTitle;
DeleteModalWithConfirmInput.Description = ModalDescription;
DeleteModalWithConfirmInput.InputHint = DeleteModalInputHint;
